import React, { createContext, useContext, useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Sidebar from './components/Sidebar';
import InstallPrompt from './components/InstallPrompt';
import SelfOrderWatcher from './components/SelfOrderWatcher';
import StaffCallWatcher from './components/StaffCallWatcher';
import { setCalendar } from './utils/neptime';
import './styles/global.css';

// Lazy-loaded routes — each splits into its own JS chunk so mobile only
// parses code for the route the user actually visits. Cuts initial bundle
// roughly in half and makes tab switches feel instant after the first visit.
// Login/Register stay eagerly imported above so the entry point is never
// blocked behind a Suspense fallback.
const Dashboard    = lazy(() => import('./pages/Dashboard'));
const OrderTaking  = lazy(() => import('./pages/OrderTaking'));
const Setup        = lazy(() => import('./pages/Setup'));
const OrderHistory = lazy(() => import('./pages/OrderHistory'));
const CostTracking = lazy(() => import('./pages/CostTracking'));
const Inventory    = lazy(() => import('./pages/Inventory'));
const Udharo       = lazy(() => import('./pages/Udharo'));
const AIInsights   = lazy(() => import('./pages/AIInsights'));
const AdminLogin   = lazy(() => import('./pages/AdminLogin'));
const AdminPanel   = lazy(() => import('./pages/AdminPanel'));
const CustomerPortal = lazy(() => import('./pages/CustomerPortal'));
const CustomerOrderPortal = lazy(() => import('./pages/CustomerOrderPortal'));

// Lightweight fallback shown for ~100-300ms while a route's chunk downloads.
// Uses existing .loading + .spinner styles from global.css so it visually
// matches the rest of the app and never causes a layout shift.
function RouteLoading() {
  return (
    <div className="loading" style={{ minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );
}

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

/* ── Restaurant settings context — single source of truth for VAT etc. ──
   Backend is authoritative. We fetch once after login and expose a refresh()
   so Setup can push updates immediately to all open pages.
   localStorage is used purely as an instant-paint fallback before the fetch
   resolves on first load. */
const SettingsContext = createContext({ vatEnabled: true, vatRate: 0.13, vatInclusive: false, calendar: 'nepali', ready: false, refresh: () => {}, setCalendarPref: () => {} });

export function useSettings() {
  return useContext(SettingsContext);
}

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ─── Two-tier API cache: in-memory L1 + localStorage L2 ──────────────────
// Goals:
//   1. Tab switches feel instant (paint cached data immediately).
//   2. Hard reloads paint instantly too (L2 = localStorage).
//   3. Data is never staler than the user can tolerate (background revalidate).
//
// Strategy: stale-while-revalidate.
//   - If cached entry is FRESH (age < ttl) → return cached, no network call.
//   - If cached entry is STALE-BUT-USABLE (ttl ≤ age < swrTtl) → return cached
//     immediately, AND fire a network request in the background to refresh.
//   - If cached entry is EXPIRED (age ≥ swrTtl) or missing → wait for network.
//
// Listeners can subscribe to a path so the UI repaints when background
// revalidation lands fresh data — used by useApiResource() below.
const _cache = {};
const _listeners = {};        // path → Set<callback>
const LS_PREFIX = 'vyapar_cache:';

function cacheTTL(path) {
  if (path.startsWith('/menu/'))         return 10 * 60_000; // menu rarely changes — 10 min
  if (path.startsWith('/settings'))      return 10 * 60_000;
  if (path.startsWith('/payment-methods')) return 10 * 60_000;
  if (path.startsWith('/reports/'))      return  3 * 60_000; // reports — 3 min
  if (path.startsWith('/dashboard'))     return     45_000;  // batch — short, contains today's orders
  if (path.startsWith('/order-taking'))  return  2 * 60_000;  // tables + menu — mirror /tables TTL
  if (path.startsWith('/tables'))        return  2 * 60_000;
  return 60_000;                                              // orders — 1 min
}
function swrTTL(path) {
  // Stale-but-usable window — we still serve from cache for this much longer
  // while a background fetch refreshes. 6× the freshness TTL is plenty.
  return cacheTTL(path) * 6;
}

// L2: localStorage hydration on module load (so first paint after a reload
// already has data). Wrapped in try/catch — localStorage can be disabled
// (private browsing variants, quota errors, etc.) and we don't care.
try {
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !k.startsWith(LS_PREFIX)) continue;
    try {
      const entry = JSON.parse(localStorage.getItem(k));
      if (entry && entry.ts && entry.data !== undefined) {
        _cache[k.slice(LS_PREFIX.length)] = entry;
      }
    } catch (_) { /* corrupt entry — ignore */ }
  }
} catch (_) { /* no localStorage — fine */ }

function persist(path, entry) {
  try { localStorage.setItem(LS_PREFIX + path, JSON.stringify(entry)); }
  catch (_) { /* quota or disabled — ignore */ }
}
function unpersist(path) {
  try { localStorage.removeItem(LS_PREFIX + path); } catch (_) {}
}

// When orders change, reports/tables/dashboard are all stale too.
// (Table status flips between occupied/available on order create/pay/cancel,
// and the /dashboard batch endpoint bundles all of these.)
const BUST_ALSO = {
  // Order mutations change table occupancy, which is derived live from
  // orders by /tables, /dashboard AND /order-taking (the Order-Taking
  // picker). All three must be invalidated so a just-seated table shows
  // occupied — and a just-paid table shows available — on the next read.
  // /udharo too: a credit sale (paid order + debtor_name) is the debtor side
  // of the Credit Book, so without this a just-recorded credit sale wouldn't
  // appear there until the cache expired.
  '/orders':          ['/reports', '/tables', '/dashboard', '/order-taking', '/udharo'],
  '/payment-methods': ['/dashboard'],
  '/tables':          ['/dashboard', '/order-taking'],
  // Menu edits (incl. drag-reorder) must refresh the Order-Taking picker,
  // which reads its item list (and order) from /order-taking.
  '/menu':            ['/order-taking'],
  // Offers are embedded in the /order-taking batch (seedSiblings re-injects
  // them into /offers). Without busting /order-taking, a just-deactivated /
  // edited / deleted offer reappears in the Order-Taking picker on the next
  // batch read — same stale-re-pollution class as the /settings bug.
  '/offers':          ['/order-taking'],
  // Adding/editing an expense must refresh the dashboard's Cash Out / Cash
  // Profit / Today's Cash Position cards (and reports) on the next read.
  // /udharo too: a credit purchase (cost_entries is_credit=1 + creditor_name)
  // is the creditor side of the Credit Book, so a just-logged credit purchase
  // must invalidate it to appear immediately.
  '/costs':           ['/dashboard', '/reports', '/udharo'],
  // Settings writes (business hours, calendar, VAT, payment-QR, notifications)
  // MUST also bust /dashboard. The /dashboard batch embeds a full settings
  // copy that seedSiblings() re-injects into /settings — so without this, a
  // just-saved setting gets clobbered by the stale dashboard copy the next
  // time the dashboard cache is served, reverting the Other-tab UI.
  '/settings':        ['/dashboard'],
};

// Bumped on every cache-busting mutation. A network GET snapshots this before
// it fires; if it changes while the request is in flight, the response reflects
// pre-mutation state and must NOT be cached/seeded — doing so would un-bust the
// entry and re-pollute /settings, reverting a just-saved value. See applyFetched().
let _bustGen = 0;
function bustPrefix(prefix) {
  _bustGen++;
  const prefixes = [prefix, ...(BUST_ALSO[prefix] || [])];
  Object.keys(_cache).forEach(k => {
    if (prefixes.some(p => k.startsWith(p))) {
      delete _cache[k];
      unpersist(k);
    }
  });
}

function notifyListeners(path, data) {
  const set = _listeners[path];
  if (set) set.forEach(cb => { try { cb(data); } catch (_) {} });
}

// /dashboard now bundles settings + cost-settings so the frontend doesn't
// have to fan out 5-6 simultaneous requests on every mount. After any
// /dashboard fetch lands, seed the sibling cache keys those payloads would
// otherwise populate — subsequent peek() calls in SettingsProvider /
// Sidebar then hit cache instantly and skip the network entirely.
// Public seam for code outside the useApi() hook to push a fully-fetched
// payload into the SWR cache (and trigger seedSiblings + listeners).
// Login uses this to fire /dashboard the moment auth succeeds, so by the
// time the React tree renders Dashboard the data is already cached and
// the SettingsProvider / Sidebar / Dashboard.loadData all hit the cache
// instead of making their own slow round-trips on a cold backend.
export function primeCache(path, data) {
  if (!path || data === undefined || data === null) return;
  const ts = Date.now();
  _cache[path] = { data, ts };
  persist(path, _cache[path]);
  seedSiblings(path, data);
  notifyListeners(path, data);
}

// Like primeCache, but for the network-fetch case: Login wants to KICK
// OFF a request before any React component is around to call api.get().
// The catch is that any later api.get(samePath) must reuse this in-flight
// request instead of firing a duplicate. By registering the promise in
// _inflightMap *before* awaiting, that's exactly what happens — the
// useApi().get() implementation already checks _inflightMap[path] before
// starting a new fetch, so primed requests get deduped automatically.
export function primeFetch(token, path) {
  if (!token || !path) return Promise.resolve(null);
  if (_inflightMap[path]) return _inflightMap[path];
  const gen = _bustGen;
  const url = `${API_BASE}${path}`;
  const promise = fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(r => (r.ok ? r.json() : null))
    .then(data => {
      if (data) applyFetched(path, data, gen);
      delete _inflightMap[path];
      return data;
    })
    .catch(() => {
      delete _inflightMap[path];
      return null; // fire-and-forget — never throw to the caller
    });
  _inflightMap[path] = promise;
  return promise;
}

function seedSiblings(path, data) {
  if (!data || typeof data !== 'object') return;
  const ts = Date.now();
  const seed = (key, value) => {
    if (value === undefined || value === null) return;
    _cache[key] = { data: value, ts };
    persist(key, _cache[key]);
    notifyListeners(key, value);
  };
  if (path.startsWith('/dashboard')) {
    seed('/settings',        data.settings);
    seed('/costs/settings',  data.costsSettings);
  } else if (path.startsWith('/order-taking')) {
    // The /order-taking batch returns all four payloads the OrderTaking
    // page used to fetch separately. Seeding lets any other component
    // (or a back-navigation to Orders later) get instant cache hits.
    seed('/tables',           data.tables);
    seed('/menu/categories',  data.categories);
    seed('/menu/items',       data.items);
    seed('/offers',           data.offers);
  }
}

// Coalesces concurrent GETs to the same path into a single network request.
// Module-scoped so it works across all components in the same tab.
const _inflightMap = {};

// Commit a freshly-fetched payload to the SWR cache (+ siblings + listeners) —
// but ONLY if no cache-busting mutation landed while the request was in flight.
// `gen` is _bustGen captured before the request fired. If it no longer matches,
// a write happened mid-flight, so this response reflects pre-write state:
// caching/seeding it would un-bust the entry and re-pollute /settings (reverting
// a just-saved VAT / calendar / hours). Drop it — the next read refetches fresh.
function applyFetched(path, data, gen) {
  if (_bustGen !== gen) return data;
  _cache[path] = { data, ts: Date.now() };
  persist(path, _cache[path]);
  seedSiblings(path, data);
  notifyListeners(path, data);
  return data;
}

export function useApi() {
  const { token, logout } = useAuth();

  const request = async (method, path, body) => {
    let res;
    try {
      res = await fetch(`${API_BASE}${path}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
    } catch (e) {
      // Network error, CORS, DNS, offline, etc.
      throw new Error('Cannot reach server. Check your internet and try again.');
    }

    // Read body once as text, then try to parse as JSON.
    // This survives HTML error pages, gateway timeouts, empty bodies, etc.
    const raw = await res.text();
    let data = null;
    if (raw) {
      try { data = JSON.parse(raw); } catch (_) { /* not JSON */ }
    }

    // Token expired / invalid → log out
    if (res.status === 401 || (res.status === 403 && data?.error?.includes('token'))) {
      logout();
      window.location.href = '/login';
      return;
    }

    if (!res.ok) {
      // JSON error from backend → use its message
      if (data?.error) throw new Error(data.error);
      // HTML error page (404 from Vercel, 500 crash, gateway timeout, etc.)
      console.error('[API non-JSON response]', { url: `${API_BASE}${path}`, status: res.status, body: raw });
      throw new Error(`Server error ${res.status} at ${API_BASE}${path}. Check console for details.`);
    }

    // OK status but body wasn't JSON — shouldn't happen but be safe
    if (data === null && raw) {
      throw new Error('Server returned an unexpected response. Please try again.');
    }
    return data;
  };

  return {
    // peek: synchronous read of whatever is currently in the cache for `path`.
    // Returns the cached payload (even if stale) or null. Use this at component
    // mount to seed state immediately so the first paint shows real data
    // instead of an empty/loading skeleton — the SWR `get()` then refreshes
    // in the background. This is the difference between LCP ≈ FCP and LCP
    // ≈ FCP + API-roundtrip.
    peek: (path) => {
      const entry = _cache[path];
      return entry ? entry.data : null;
    },

    // GET: stale-while-revalidate
    get: (path) => {
      const now = Date.now();
      const ttl = cacheTTL(path);
      const swr = swrTTL(path);
      const cached = _cache[path];

      // FRESH — serve from cache, no network at all.
      if (cached && now - cached.ts < ttl) {
        return Promise.resolve(cached.data);
      }

      // STALE-BUT-USABLE — serve cached, refresh in background.
      if (cached && now - cached.ts < swr) {
        // Fire revalidation but only if no other revalidation is in flight.
        if (!_inflightMap[path]) {
          const gen = _bustGen;
          _inflightMap[path] = request('GET', path).then(data => {
            applyFetched(path, data, gen);
            delete _inflightMap[path];
            return data;
          }).catch(e => {
            delete _inflightMap[path];
            // Background failure is non-fatal — keep serving stale cache.
            console.warn('[API revalidate]', path, e?.message);
          });
        }
        return Promise.resolve(cached.data);
      }

      // EXPIRED or MISSING — must wait for network. Coalesce parallel callers.
      if (_inflightMap[path]) return _inflightMap[path];
      const gen = _bustGen;
      _inflightMap[path] = request('GET', path).then(data => {
        applyFetched(path, data, gen);
        delete _inflightMap[path];
        return data;
      }).catch(e => {
        delete _inflightMap[path];
        throw e;
      });
      return _inflightMap[path];
    },

    // Mutations: bust the cache for the affected resource group, then fetch
    post: (path, body) => {
      bustPrefix('/' + path.split('/')[1]);
      return request('POST', path, body);
    },
    put: (path, body) => {
      bustPrefix('/' + path.split('/')[1]);
      return request('PUT', path, body);
    },
    patch: (path, body) => {
      bustPrefix('/' + path.split('/')[1]);
      return request('PATCH', path, body);
    },
    del: (path) => {
      bustPrefix('/' + path.split('/')[1]);
      return request('DELETE', path);
    },
  };
}

function AppLayout({ children }) {
  return (
    <div className="app-layout">
      <Sidebar />
      <main className="app-main">{children}</main>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

/* Staff cannot access Sales Reports — redirect to /orders */
function OwnerManagerRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'staff') return <Navigate to="/orders" replace />;
  return <AppLayout>{children}</AppLayout>;
}

/* ─── Admin sub-app (completely separate from restaurant auth) ─── */
function AdminApp() {
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem('vyapar_admin_token'));

  const handleLogin = (token) => {
    setAdminToken(token);
    localStorage.setItem('vyapar_admin_token', token);
  };
  const handleLogout = () => {
    setAdminToken(null);
    localStorage.removeItem('vyapar_admin_token');
  };

  if (!adminToken) return <AdminLogin onLogin={handleLogin} />;
  return <AdminPanel token={adminToken} onLogout={handleLogout} />;
}

/* Fetches /settings once after login and provides VAT settings to the tree.
   Lives inside the AuthContext provider so it can use the auth token. */
function SettingsProvider({ children }) {
  const { token } = useAuth();
  // Initial guess from localStorage for instant first paint — replaced as soon
  // as the backend responds.
  const initial = (() => {
    try {
      const ls = JSON.parse(localStorage.getItem('vyapar_settings') || '{}');
      return { vatEnabled: ls.vatEnabled !== false, vatRate: ls.vatRate ?? 0.13, vatInclusive: !!ls.vatInclusive };
    } catch { return { vatEnabled: true, vatRate: 0.13, vatInclusive: false }; }
  })();
  const [vatEnabled, setVatEnabled] = useState(initial.vatEnabled);
  const [vatRate, setVatRate]       = useState(initial.vatRate);
  const [vatInclusive, setVatInclusive] = useState(initial.vatInclusive);
  const [calendar, setCalState]     = useState(() => {
    try { return JSON.parse(localStorage.getItem('vyapar_settings') || '{}').calendar === 'english' ? 'english' : 'nepali'; }
    catch { return 'nepali'; }
  });
  const [ready, setReady]           = useState(false);

  // Apply a calendar preference both to the display layer (neptime module) and
  // to context state (so date-rendering components re-render on change).
  const applyCalendar = React.useCallback((pref) => {
    const cal = pref === 'english' ? 'english' : 'nepali';
    setCalendar(cal);   // neptime module-level — drives fmtDate/fmtDateTime
    setCalState(cal);
  }, []);
  // Keep the module layer in sync on first mount / instant paint.
  useEffect(() => { setCalendar(calendar); }, [calendar]);

  const refresh = React.useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/settings`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const s = await res.json();
      const enabled = s.vat_enabled !== 0;
      const rate    = s.vat_rate ?? 0.13;
      const incl    = !!s.vat_inclusive;
      setVatEnabled(enabled);
      setVatRate(rate);
      setVatInclusive(incl);
      applyCalendar(s.calendar_preference);
      setReady(true);
      // Cache for next page-load fallback
      localStorage.setItem('vyapar_settings', JSON.stringify({ vatEnabled: enabled, vatRate: rate, vatInclusive: incl, calendar: s.calendar_preference === 'english' ? 'english' : 'nepali' }));
      _cache['/settings'] = { data: s, ts: Date.now() };
      persist('/settings', _cache['/settings']);
    } catch (_) { /* offline / network blip — keep current values */ }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    // Try the shared in-memory cache first. /dashboard now bundles
    // settings, and an earlier session may have populated the cache via
    // localStorage hydration on module load. Either way, when peek() hits
    // we apply state from cache and skip the /settings network call
    // entirely — one less request piling onto a cold backend.
    const cached = _cache['/settings'];
    if (cached && cached.data) {
      const s = cached.data;
      const enabled = s.vat_enabled !== 0;
      const rate    = s.vat_rate ?? 0.13;
      setVatEnabled(enabled);
      setVatRate(rate);
      setVatInclusive(!!s.vat_inclusive);
      applyCalendar(s.calendar_preference);
      setReady(true);
      // Revalidate only if the cache entry is older than the /settings TTL.
      if (Date.now() - cached.ts < cacheTTL('/settings')) return;
    }
    // Listen for the seedSiblings broadcast — if a /dashboard fetch arrives
    // before our refresh() does, we still pick the settings up from there.
    const onSiblingArrived = (s) => {
      if (!s) return;
      const enabled = s.vat_enabled !== 0;
      const rate    = s.vat_rate ?? 0.13;
      setVatEnabled(enabled);
      setVatRate(rate);
      setVatInclusive(!!s.vat_inclusive);
      applyCalendar(s.calendar_preference);
      setReady(true);
    };
    if (!_listeners['/settings']) _listeners['/settings'] = new Set();
    _listeners['/settings'].add(onSiblingArrived);
    refresh();
    return () => { _listeners['/settings']?.delete(onSiblingArrived); };
  }, [token, refresh]);

  return (
    <SettingsContext.Provider value={{ vatEnabled, vatRate, vatInclusive, calendar, ready, refresh, setCalendarPref: applyCalendar }}>
      {children}
    </SettingsContext.Provider>
  );
}

/* ─────────────────────────────────────────────────────────
   Inactivity auto-logout
   Logs the user out after IDLE_MS of no interaction.
   - Activity events (mousemove, keydown, touchstart, click, scroll)
     reset the timer + write a timestamp to localStorage.
   - On page load / tab focus, we compare the stored timestamp against
     "now" and force a logout if the gap exceeds the limit. This makes
     it survive refreshes, tab-switches, and laptop-sleep.
   - Only armed while a token exists.
   ───────────────────────────────────────────────────────── */
// Inactivity auto-logout. Whether it runs is decided PER USER: users with the
// "keep me logged in" preference off get the 60-min idle timeout; users with it
// on stay signed in until they explicitly tap "Log out". The `enabled` arg to
// useIdleLogout() carries that per-user decision.
const IDLE_MS = 60 * 60 * 1000;          // 60 minutes
const ACTIVITY_KEY = 'vyapar_last_activity';
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'click'];

function useIdleLogout(token, logout, enabled) {
  const timerRef = useRef(null);
  const lastWriteRef = useRef(0);

  const doLogout = useCallback(() => {
    logout();
    localStorage.removeItem(ACTIVITY_KEY);
    // Bounce to /login unless already on a public page (login/register/customer portals)
    const path = window.location.pathname;
    const publicPath = path.startsWith('/login') || path.startsWith('/register') || path.startsWith('/menu') || path.startsWith('/order');
    if (!publicPath) window.location.href = '/login';
  }, [logout]);

  const armTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(doLogout, IDLE_MS);
  }, [doLogout]);

  const onActivity = useCallback(() => {
    if (!token) return;
    const now = Date.now();
    // Throttle localStorage writes — once every 30s is plenty.
    if (now - lastWriteRef.current > 30_000) {
      localStorage.setItem(ACTIVITY_KEY, String(now));
      lastWriteRef.current = now;
    }
    armTimer();
  }, [token, armTimer]);

  useEffect(() => {
    // Per-user "keep me logged in" is on → never arm the timer; clear any
    // stale activity marker so a later toggle-off starts fresh.
    if (!enabled) {
      if (timerRef.current) clearTimeout(timerRef.current);
      localStorage.removeItem(ACTIVITY_KEY);
      return;
    }
    if (!token) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // On mount / token change: if last activity was over the limit ago, log out immediately.
    const last = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);
    if (last && Date.now() - last > IDLE_MS) {
      doLogout();
      return;
    }

    // Seed timestamp + arm the countdown.
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    lastWriteRef.current = Date.now();
    armTimer();

    // Listen for user activity (passive listeners — never block scroll/touch).
    ACTIVITY_EVENTS.forEach(ev =>
      window.addEventListener(ev, onActivity, { passive: true })
    );

    // When tab regains focus, re-check the stored timestamp — covers laptop sleep.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        const t = parseInt(localStorage.getItem(ACTIVITY_KEY) || '0', 10);
        if (t && Date.now() - t > IDLE_MS) doLogout();
        else armTimer();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [token, enabled, doLogout, armTimer, onActivity]);
}

export default function App() {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('vyapar_user')); } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('vyapar_token'));

  const login = (userData, tokenData) => {
    setUser(userData);
    setToken(tokenData);
    localStorage.setItem('vyapar_user', JSON.stringify(userData));
    localStorage.setItem('vyapar_token', tokenData);
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('vyapar_user');
    localStorage.removeItem('vyapar_token');
    localStorage.removeItem(ACTIVITY_KEY);
  };

  // Merge a partial update into the stored user (e.g. the Account toggle
  // flipping keep_logged_in) so the change takes effect without a re-login.
  const updateUser = (patch) => {
    setUser(prev => {
      const next = { ...(prev || {}), ...patch };
      localStorage.setItem('vyapar_user', JSON.stringify(next));
      return next;
    });
  };

  // Arm the 60-min inactivity timer only for users who haven't opted into a
  // persistent ("keep me logged in") session.
  useIdleLogout(token, logout, !user?.keep_logged_in);

  // The staff notification watchers (new-order + call-staff popups) must NEVER
  // appear on the public customer-portal routes (/c/:token, /m/:token), even if
  // a staff happens to be logged in on the same device (sessions are now
  // persistent). The customer portal is always a fresh page load, so a
  // path check at render is sufficient.
  const onCustomerRoute = typeof window !== 'undefined'
    && (window.location.pathname.startsWith('/c/') || window.location.pathname.startsWith('/m/'));
  const showStaffWatchers = !!user && !onCustomerRoute;

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser }}>
      <SettingsProvider>
        <InstallPrompt />
        {showStaffWatchers && <SelfOrderWatcher />}
        {showStaffWatchers && <StaffCallWatcher />}
        <BrowserRouter>
          <Suspense fallback={<RouteLoading />}>
            <Routes>
              <Route path="/login"    element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
              <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password"  element={<ResetPassword />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/orders" element={<ProtectedRoute><OrderTaking /></ProtectedRoute>} />
              <Route path="/setup" element={<ProtectedRoute><Setup /></ProtectedRoute>} />
              <Route path="/history" element={<OwnerManagerRoute><OrderHistory /></OwnerManagerRoute>} />
              <Route path="/costs"   element={<OwnerManagerRoute><CostTracking /></OwnerManagerRoute>} />
              <Route path="/inventory" element={<OwnerManagerRoute><Inventory /></OwnerManagerRoute>} />
              <Route path="/udharo"    element={<OwnerManagerRoute><Udharo /></OwnerManagerRoute>} />
              <Route path="/ai"      element={<OwnerManagerRoute><AIInsights /></OwnerManagerRoute>} />
              <Route path="/admin" element={<AdminApp />} />
              {/* Public customer self-order portal — no auth, no AppShell */}
              {/* NEW per-order model: staff issues a QR per order */}
              <Route path="/c/:customerToken" element={<CustomerOrderPortal />} />
              {/* LEGACY per-restaurant model — kept so old printed QRs still work */}
              <Route path="/m/:qrToken/t/:tableId" element={<CustomerPortal />} />
              <Route path="/m/:qrToken" element={<CustomerPortal />} />
              <Route path="*" element={<Navigate to={user ? '/dashboard' : '/login'} replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </SettingsProvider>
    </AuthContext.Provider>
  );
}
